import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { financeAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  ArrowUpTrayIcon,
  DocumentChartBarIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsRightLeftIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

// ── Report types (QuickBooks-focused) ────────────────────────────────────────

const REPORT_TYPES = [
  { value: 'profit_loss',         label: 'Profit & Loss',        color: 'bg-green-100 text-green-700',   icon: '📈' },
  { value: 'balance_sheet',       label: 'Balance Sheet',        color: 'bg-blue-100 text-blue-700',     icon: '⚖️' },
  { value: 'cash_flow',           label: 'Cash Flow Statement',  color: 'bg-cyan-100 text-cyan-700',     icon: '💧' },
  { value: 'accounts_receivable', label: 'Accounts Receivable',  color: 'bg-emerald-100 text-emerald-700', icon: '📥' },
  { value: 'accounts_payable',    label: 'Accounts Payable',     color: 'bg-orange-100 text-orange-700', icon: '📤' },
  { value: 'stock_inventory',     label: 'Stock / Inventory',    color: 'bg-purple-100 text-purple-700', icon: '📦' },
  { value: 'solvency',            label: 'Solvency Report',      color: 'bg-indigo-100 text-indigo-700', icon: '🛡️' },
  { value: 'budget_vs_actual',    label: 'Budget vs Actual',     color: 'bg-amber-100 text-amber-700',   icon: '📊' },
];

const TYPE_MAP = Object.fromEntries(REPORT_TYPES.map(t => [t.value, t]));

const STATUS_CONFIG = {
  good:     { cls: 'bg-green-50 border-green-200 text-green-800',  badgeCls: 'bg-green-100 text-green-700',  icon: <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" /> },
  warning:  { cls: 'bg-amber-50 border-amber-200 text-amber-800',  badgeCls: 'bg-amber-100 text-amber-700',  icon: <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" /> },
  critical: { cls: 'bg-red-50 border-red-200 text-red-800',        badgeCls: 'bg-red-100 text-red-700',      icon: <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" /> },
};

const ALERT_CONFIG = {
  error:   { cls: 'bg-red-50 border-red-200 text-red-800',        icon: <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" /> },
  warning: { cls: 'bg-amber-50 border-amber-200 text-amber-800',  icon: <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" /> },
  info:    { cls: 'bg-blue-50 border-blue-200 text-blue-800',     icon: <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0" /> },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function Spinner({ size = 'sm' }) {
  const s = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-6 h-6' : 'w-8 h-8';
  return <div className={`${s} border-2 border-current border-t-transparent rounded-full animate-spin`} />;
}

function TypeBadge({ type }) {
  const t = TYPE_MAP[type] || { label: type, color: 'bg-gray-100 text-gray-700', icon: '📄' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>
      <span>{t.icon}</span>{t.label}
    </span>
  );
}

// ── MetricCard ────────────────────────────────────────────────────────────────

function MetricCard({ metric }) {
  const cfg = STATUS_CONFIG[metric.status] || STATUS_CONFIG.warning;
  return (
    <div className={`border rounded-xl p-4 flex flex-col gap-1 ${cfg.cls}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide opacity-70">{metric.name}</span>
        {cfg.icon}
      </div>
      <div className="text-2xl font-bold leading-tight">{metric.value}</div>
      {metric.description && (
        <div className="text-xs opacity-75 leading-snug">{metric.description}</div>
      )}
    </div>
  );
}

function AlertBanner({ alert }) {
  const cfg = ALERT_CONFIG[alert.level] || ALERT_CONFIG.info;
  return (
    <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${cfg.cls}`}>
      {cfg.icon}
      <span>{alert.message}</span>
    </div>
  );
}

// ── DataTable ────────────────────────────────────────────────────────────────

function DataTable({ table }) {
  if (!table?.headers?.length || !table?.rows?.length) return null;
  return (
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{table.title}</h4>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-primary-600 text-white">
            <tr>
              {table.headers.map((h, i) => (
                <th key={i} className="text-left px-3 py-2 text-xs font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-xs text-gray-700 border-t border-gray-100">{String(cell ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── AnalysisDashboard ─────────────────────────────────────────────────────────

function AnalysisDashboard({ analysis, report, onOpenChat, onDownloadPdf }) {
  const [expandedSection, setExpandedSection] = useState(null);

  if (!analysis) return null;

  const metrics = analysis.metrics || [];
  const alerts = analysis.alerts || [];
  const sections = analysis.sections || [];
  const recs = analysis.recommendations || [];
  const tables = analysis.table_data || [];

  return (
    <div className="space-y-6">
      {/* Summary */}
      {analysis.summary && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Executive Summary</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{analysis.summary}</p>
        </div>
      )}

      {/* Metrics grid */}
      {metrics.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Key Metrics</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {metrics.map((m, i) => <MetricCard key={i} metric={m} />)}
          </div>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Alerts & Observations</h3>
          <div className="space-y-2">
            {alerts.map((a, i) => <AlertBanner key={i} alert={a} />)}
          </div>
        </div>
      )}

      {/* Tables */}
      {tables.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Supporting Tables</h3>
          {tables.map((t, i) => <DataTable key={i} table={t} />)}
        </div>
      )}

      {/* Sections (accordion) */}
      {sections.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Detailed Analysis</h3>
          <div className="space-y-2">
            {sections.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === i ? null : i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-800 bg-white hover:bg-gray-50 transition-colors"
                >
                  {s.title}
                  {expandedSection === i
                    ? <ChevronUpIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {expandedSection === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-gray-600 leading-relaxed border-t border-gray-100 whitespace-pre-wrap">
                    {s.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recommendations</h3>
          <ul className="space-y-2">
            {recs.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={onDownloadPdf}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Download PDF
        </button>
        <button
          onClick={onOpenChat}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
        >
          <ChatBubbleLeftRightIcon className="w-4 h-4" />
          Ask AI about this report
        </button>
      </div>
    </div>
  );
}

// ── ChatPanel ─────────────────────────────────────────────────────────────────

function ChatPanel({ report, onClose }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (report?.chat_history_json) {
      try { setHistory(JSON.parse(report.chat_history_json)); } catch { setHistory([]); }
    }
  }, [report?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);
    setHistory(h => [...h, { role: 'user', content: msg }]);
    try {
      const res = await financeAPI.chat(report.id, msg);
      setHistory(res.data.chat_history);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chat error');
      setHistory(h => h.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    setClearing(true);
    try {
      await financeAPI.clearChat(report.id);
      setHistory([]);
      toast.success('Chat cleared');
    } catch { toast.error('Failed to clear chat'); }
    finally { setClearing(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">AI Chat</h3>
          <p className="text-xs text-gray-500 truncate max-w-[200px]">{report.report_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <button
              onClick={clearChat}
              disabled={clearing}
              className="text-xs text-gray-400 hover:text-red-600 transition-colors"
            >
              {clearing ? <Spinner /> : 'Clear'}
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {history.length === 0 && (
          <div className="text-center text-sm text-gray-400 mt-8">
            <ChatBubbleLeftRightIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>Ask anything about this report.</p>
            <p className="text-xs mt-1 opacity-70">e.g. "Which customer owes the most?" or "Explain the variance"</p>
          </div>
        )}
        {history.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-xl rounded-bl-sm text-sm flex items-center gap-2">
              <Spinner size="sm" /> Thinking…
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-3 border-t border-gray-200 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about this report… (Enter to send)"
            rows={2}
            disabled={sending}
            className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="p-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 transition-colors flex-shrink-0"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CompareModal ──────────────────────────────────────────────────────────────

function CompareModal({ reports, onClose }) {
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restrictedType, setRestrictedType] = useState('');

  const analysed = reports.filter(r => r.has_analysis);

  // When user picks Report A, restrict Report B to the same type
  useEffect(() => {
    if (!idA) { setRestrictedType(''); return; }
    const a = analysed.find(r => String(r.id) === String(idA));
    setRestrictedType(a?.report_type || '');
  }, [idA, analysed]);

  const eligibleForB = restrictedType
    ? analysed.filter(r => r.report_type === restrictedType && String(r.id) !== String(idA))
    : analysed.filter(r => String(r.id) !== String(idA));

  const run = async () => {
    if (!idA || !idB || idA === idB) {
      toast.error('Select two different reports to compare');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await financeAPI.compare(Number(idA), Number(idB));
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const cmp = result?.comparison || {};
  const TREND_COLOR = { improving: 'text-green-600', declining: 'text-red-600', mixed: 'text-amber-600', stable: 'text-blue-600' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Compare Reports</h2>
            <p className="text-xs text-gray-500 mt-0.5">Pick two reports of the same type to see side-by-side trends</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Report A (earlier period)</label>
              <select
                value={idA}
                onChange={e => { setIdA(e.target.value); setIdB(''); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="">Select a report…</option>
                {analysed.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.report_name} ({TYPE_MAP[r.report_type]?.label || r.report_type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Report B (later period)</label>
              <select
                value={idB}
                onChange={e => setIdB(e.target.value)}
                disabled={!idA}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-gray-50 disabled:opacity-60"
              >
                <option value="">{idA ? 'Select a report…' : 'Pick Report A first'}</option>
                {eligibleForB.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.report_name} ({TYPE_MAP[r.report_type]?.label || r.report_type})
                  </option>
                ))}
              </select>
              {restrictedType && (
                <p className="text-xs text-gray-400 mt-1">Filtered to {TYPE_MAP[restrictedType]?.label || restrictedType} reports</p>
              )}
            </div>
          </div>

          <button
            onClick={run}
            disabled={loading || !idA || !idB}
            className="w-full py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner /> Comparing…</> : <><ArrowsRightLeftIcon className="w-4 h-4" /> Run Comparison</>}
          </button>

          {result && (
            <div className="space-y-5">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Overall Trend</h3>
                  <span className={`text-sm font-bold capitalize ${TREND_COLOR[cmp.overall_trend] || ''}`}>
                    {cmp.overall_trend}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{cmp.summary}</p>
              </div>

              {(cmp.differences || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Metric Comparison</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">Metric</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">{result.report_a?.name || 'A'}</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">{result.report_b?.name || 'B'}</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">Change</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">Trend</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-100">Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cmp.differences || []).map((d, i) => {
                          const tcolor = d.trend === 'improved' ? 'text-green-600' : d.trend === 'declined' ? 'text-red-600' : 'text-gray-500';
                          const change = d.change_pct || '—';
                          const changeColor = change.startsWith('+') ? 'text-green-600' : change.startsWith('-') ? 'text-red-600' : 'text-gray-500';
                          return (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-3 py-2 border border-gray-100 font-medium text-gray-800">{d.metric}</td>
                              <td className="px-3 py-2 border border-gray-100 text-gray-700">{d.value_a}</td>
                              <td className="px-3 py-2 border border-gray-100 text-gray-700">{d.value_b}</td>
                              <td className={`px-3 py-2 border border-gray-100 font-semibold ${changeColor}`}>{change}</td>
                              <td className={`px-3 py-2 border border-gray-100 capitalize font-medium ${tcolor}`}>{d.trend}</td>
                              <td className="px-3 py-2 border border-gray-100 text-gray-600 text-xs">{d.comment}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(cmp.key_insights || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Key Insights</h3>
                  <ul className="space-y-1.5">
                    {cmp.key_insights.map((k, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">★</span>
                        {k}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(cmp.recommendations || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Recommendations</h3>
                  <ul className="space-y-1.5">
                    {cmp.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ReportRow ─────────────────────────────────────────────────────────────────

function ReportRow({ report, onDelete, onOpenChat, onSelectAnalysis, isSelectedForAnalysis, onDownloadPdf }) {
  const [deleting, setDeleting] = useState(false);
  const [reanalysing, setReanalysing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${report.report_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await financeAPI.delete(report.id);
      toast.success('Report deleted');
      onDelete(report.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleReanalyse = async () => {
    setReanalysing(true);
    try {
      await financeAPI.reanalyse(report.id);
      toast.success('Re-analysis started — refreshing…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-analysis failed');
    } finally {
      setReanalysing(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownloadPdf(report);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`bg-white border rounded-xl p-4 transition-all ${
        isSelectedForAnalysis ? 'border-primary-400 shadow-md' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TypeBadge type={report.report_type} />
            {!report.has_analysis && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                <ClockIcon className="w-3 h-3" /> Analysing…
              </span>
            )}
          </div>
          <h3 className="mt-1.5 text-sm font-semibold text-gray-900 truncate">{report.report_name}</h3>
          {report.original_filename && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{report.original_filename}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date(report.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {report.has_analysis && (
            <>
              <button
                onClick={() => onSelectAnalysis(report)}
                className={`px-2.5 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                  isSelectedForAnalysis
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                }`}
              >
                {isSelectedForAnalysis ? 'Viewing' : 'View Analysis'}
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Download analysis as PDF"
              >
                {downloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="w-4 h-4" />}
              </button>
              <button
                onClick={() => onOpenChat(report)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                title="Chat about this report"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={handleReanalyse}
            disabled={reanalysing}
            className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            title="Re-analyse"
          >
            {reanalysing ? <Spinner size="sm" /> : <ArrowPathIcon className="w-4 h-4" />}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            {deleting ? <Spinner size="sm" /> : <TrashIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── UploadForm ────────────────────────────────────────────────────────────────

function UploadForm({ departmentId, onUploaded }) {
  const { canModule } = useAuth();
  const canAdd = canModule('finance', 'add');
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (!reportName) {
      setReportName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !reportName.trim() || !reportType) {
      toast.error('Please fill in all fields and select a file');
      return;
    }
    setUploading(true);
    try {
      const res = await financeAPI.upload(departmentId, reportName.trim(), reportType, file);
      toast.success('Report uploaded — analysis starting…');
      onUploaded(res.data);
      setReportName('');
      setReportType('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
        <ArrowUpTrayIcon className="w-4 h-4 text-primary-600" />
        Upload QuickBooks Report
      </h3>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Report Type</label>
        <div className="grid grid-cols-2 gap-2">
          {REPORT_TYPES.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setReportType(t.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                reportType === t.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700 ring-1 ring-primary-400'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span>{t.icon}</span>
              <span className="truncate text-left">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Report Name</label>
        <input
          type="text"
          value={reportName}
          onChange={e => setReportName(e.target.value)}
          placeholder="e.g. Q1 2026 P&L"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">File (PDF or Excel from QuickBooks)</label>
        <div
          className={`border-2 border-dashed rounded-lg px-4 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${
            file ? 'border-primary-300 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => fileRef.current?.click()}
        >
          <DocumentChartBarIcon className={`w-5 h-5 flex-shrink-0 ${file ? 'text-primary-500' : 'text-gray-400'}`} />
          {file ? (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-700 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-gray-600 leading-tight">Click to select file</p>
              <p className="text-xs text-gray-400">PDF, XLSX, DOCX, or TXT</p>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.docx,.txt"
            className="hidden"
            onChange={handleFile}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={uploading || !file || !reportName.trim() || !reportType || !canAdd}
        title={canAdd ? '' : "🔒 You don't have permission"}
        className="w-full py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {uploading ? <><Spinner /> Uploading & Analysing…</> : <><ArrowUpTrayIcon className="w-4 h-4" /> Upload & Analyse</>}
      </button>
    </form>
  );
}

// ── HistoryFilters ────────────────────────────────────────────────────────────

function HistoryFilters({ filters, setFilters, onClear }) {
  const hasActiveFilter = filters.reportType || filters.dateFrom || filters.dateTo;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <FunnelIcon className="w-3.5 h-3.5" /> Filter History
        </div>
        {hasActiveFilter && (
          <button
            onClick={onClear}
            className="text-xs text-gray-400 hover:text-red-600 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>
      <select
        value={filters.reportType}
        onChange={e => setFilters({ ...filters, reportType: e.target.value })}
        className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400"
      >
        <option value="">All report types</option>
        {REPORT_TYPES.map(t => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase">From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters({ ...filters, dateFrom: e.target.value })}
            className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase">To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters({ ...filters, dateTo: e.target.value })}
            className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FinanceReports() {
  const { departmentId } = useParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [chatReport, setChatReport] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [filters, setFilters] = useState({ reportType: '', dateFrom: '', dateTo: '' });
  const pollRef = useRef(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await financeAPI.list(departmentId, filters);
      setReports(res.data);
      setSelectedReport(prev => {
        if (!prev) return prev;
        const updated = res.data.find(r => r.id === prev.id);
        return updated || prev;
      });
    } catch (err) {
      console.error('Failed to load finance reports', err);
    }
  }, [departmentId, filters]);

  useEffect(() => {
    setLoading(true);
    fetchReports().finally(() => setLoading(false));
  }, [fetchReports]);

  useEffect(() => {
    const hasPending = reports.some(r => !r.has_analysis);
    if (hasPending) {
      pollRef.current = setInterval(fetchReports, 5000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [reports, fetchReports]);

  const handleUploaded = (report) => {
    setReports(prev => [report, ...prev]);
  };

  const handleDelete = (id) => {
    setReports(prev => prev.filter(r => r.id !== id));
    if (selectedReport?.id === id) {
      setSelectedReport(null);
      setSelectedAnalysis(null);
    }
  };

  const handleSelectAnalysis = (report) => {
    if (selectedReport?.id === report.id) {
      setSelectedReport(null);
      setSelectedAnalysis(null);
      return;
    }
    financeAPI.get(report.id).then(res => {
      const full = res.data;
      setSelectedReport(full);
      try {
        setSelectedAnalysis(full.analysis_json ? JSON.parse(full.analysis_json) : null);
      } catch {
        setSelectedAnalysis(null);
      }
    }).catch(() => toast.error('Failed to load report details'));
  };

  const handleOpenChat = (report) => {
    financeAPI.get(report.id).then(res => setChatReport(res.data)).catch(() => setChatReport(report));
  };

  const handleDownloadPdf = async (report) => {
    try {
      const res = await financeAPI.downloadPdf(report.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (report.report_name || 'analysis').replace(/[^a-z0-9_\- ]/gi, '_').replace(/\s+/g, '_');
      a.download = `${safeName}_analysis.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF download failed');
    }
  };

  const clearFilters = () => setFilters({ reportType: '', dateFrom: '', dateTo: '' });

  const analysedCount = reports.filter(r => r.has_analysis).length;
  const hasFilters = filters.reportType || filters.dateFrom || filters.dateTo;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left column */}
      <div className={`flex flex-col gap-4 overflow-y-auto p-6 ${selectedReport || chatReport ? 'w-96 flex-shrink-0 border-r border-gray-200' : 'flex-1'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DocumentChartBarIcon className="w-6 h-6 text-blue-600" />
              Financial Reports
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Upload QuickBooks reports for AI-powered analysis</p>
          </div>
          {analysedCount >= 2 && (
            <button
              onClick={() => setShowCompare(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowsRightLeftIcon className="w-4 h-4" />
              Compare
            </button>
          )}
        </div>

        <UploadForm departmentId={departmentId} onUploaded={handleUploaded} />

        <HistoryFilters filters={filters} setFilters={setFilters} onClear={clearFilters} />

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700">
              Report History
              {reports.length > 0 && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  {reports.length} report{reports.length !== 1 ? 's' : ''}{hasFilters ? ' (filtered)' : ''}
                </span>
              )}
            </h2>
            <button
              onClick={fetchReports}
              className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <DocumentChartBarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{hasFilters ? 'No reports match the filters' : 'No reports yet'}</p>
              <p className="text-xs mt-1">{hasFilters ? 'Try clearing the filters' : 'Upload a QuickBooks export to get started'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map(r => (
                <ReportRow
                  key={r.id}
                  report={r}
                  onDelete={handleDelete}
                  onOpenChat={handleOpenChat}
                  onSelectAnalysis={handleSelectAnalysis}
                  isSelectedForAnalysis={selectedReport?.id === r.id}
                  onDownloadPdf={handleDownloadPdf}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Analysis panel */}
      {selectedReport && !chatReport && (
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{selectedReport.report_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <TypeBadge type={selectedReport.report_type} />
                {selectedReport.original_filename && (
                  <span className="text-xs text-gray-400">{selectedReport.original_filename}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => { setSelectedReport(null); setSelectedAnalysis(null); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {selectedAnalysis ? (
            <AnalysisDashboard
              analysis={selectedAnalysis}
              report={selectedReport}
              onOpenChat={() => handleOpenChat(selectedReport)}
              onDownloadPdf={() => handleDownloadPdf(selectedReport)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Spinner size="lg" />
              <p className="mt-4 text-sm">Analysis in progress…</p>
              <p className="text-xs mt-1">This usually takes 10-30 seconds</p>
            </div>
          )}
        </div>
      )}

      {/* Chat panel */}
      {chatReport && (
        <div className="w-96 flex-shrink-0 border-l border-gray-200 flex flex-col bg-white">
          <ChatPanel report={chatReport} onClose={() => setChatReport(null)} />
        </div>
      )}

      {/* Compare modal */}
      {showCompare && (
        <CompareModal reports={reports} onClose={() => setShowCompare(false)} />
      )}
    </div>
  );
}
